import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./index.css";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import {
  Layout,
  List,
  Button,
  Drawer,
  Select,
  Row,
  Typography,
  Breadcrumb,
  Divider,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { DocumentTitle, ProductCard } from "../../components";
import {
  CategoryType,
  Color,
  Size,
  Sort,
  sortFormString,
} from "../../../domain";
import I18n from "../../i18n";
import Filters from "./components/Filters";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { loadProdutcsList } from "../../../application/products-list-slice";
import { useQuery } from "../../hooks";
import NotFound from "../NotFound";
import useWindowDimensions from "../../hooks/useWindowDimensions";

const { Sider, Content } = Layout;

type CategoryParams = {
  id: string;
  title: string;
};

const siderWith = 275;
export default function Category() {
  const dispatch = useAppDispatch();
  const query = useQuery();
  const location = useLocation();
  const history = useHistory();
  const { id, title } = useParams<CategoryParams>();
  const { width } = useWindowDimensions();
  const [contentMarginRight, setContentMarginRight] = useState(
    isMobile ? 0 : siderWith
  );
  const [areMobileFiltersVisible, setAreMobileFiltersVisible] = useState(false);
  const {
    loading,
    products,
    productsCount,
    pageSize,
    failure,
  } = useAppSelector((state) => state.productsList);
  const { generalCategories } = useAppSelector((state) => state.categories);

  const openMobileFilters = () => setAreMobileFiltersVisible(true);
  const closeMobileFilters = () => setAreMobileFiltersVisible(false);

  let page: number;
  try {
    const pageStr = query.get("page");
    page = parseInt(pageStr === null ? "1" : pageStr);
  } catch (_) {
    page = 1;
  }

  const siezesJson = query.get("sizes");
  const selectedSizes: Size[] =
    siezesJson === null
      ? []
      : JSON.parse(siezesJson).map((json: any) => new Size().deserialize(json));
  const colorsJson = query.get("colors");
  const selectedColors: Color[] =
    colorsJson === null
      ? []
      : JSON.parse(colorsJson).map((json: any) =>
        new Color().deserialize(json)
      );

  let selectedSort: Sort;
  const sortJson = query.get("sort");
  if (sortJson !== null) selectedSort = sortFormString(sortJson);
  else selectedSort = Sort.date;

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    const ids = id.split("-");
    let categoryType: CategoryType;
    if (ids.length === 1) categoryType = "generalCategory";
    else if (ids.length === 2) categoryType = "subCategory";
    else categoryType = "category";
    stableDispatch(
      loadProdutcsList(
        ids[ids.length - 1],
        categoryType,
        page,
        siezesJson === null
          ? []
          : JSON.parse(siezesJson).map((json: any) =>
            new Size().deserialize(json)
          ),
        colorsJson === null
          ? []
          : JSON.parse(colorsJson).map((json: any) =>
            new Color().deserialize(json)
          ),
        selectedSort
      )
    );
  }, [stableDispatch, id, page, siezesJson, colorsJson, selectedSort]);

  if (loading === "failed") {
    const productNotFound = failure?.maybeMap({
      categoryNotFoundFailure: () => true,
      orElse: () => false,
    });
    if (productNotFound) return <NotFound />;
  }

  const getCaytegoryLink = (categoryId?: string, categoryTitle?: string) => {
    return `/category/${categoryId}/${categoryTitle ? encodeURIComponent(categoryTitle) : ""
      }`;
  };

  const renderBreadCrumb = () => {
    const ids = id.split("-");
    const generalCategory = generalCategories.find((e) => e.id === ids[0]);
    let subCategory;
    if (ids.length >= 2)
      subCategory = generalCategory?.subCategories.find((e) => e.id === ids[1]);
    let category;
    if (ids.length >= 2)
      category = subCategory?.categories.find((e) => e.id === ids[2]);
    return (
      <Breadcrumb separator={<LeftOutlined />} style={{ padding: "0 0 8px 0" }}>
        <Breadcrumb.Item>
          <Link to="/">فروشگاه لِنا استایل</Link>
        </Breadcrumb.Item>
        {generalCategory !== undefined ? (
          <Fragment>
            <Breadcrumb.Item>
              <Link
                to={getCaytegoryLink(generalCategory.id, generalCategory.title)}
              >
                {generalCategory.title}
              </Link>
            </Breadcrumb.Item>
            {subCategory !== undefined ? (
              <Fragment>
                <Breadcrumb.Item>
                  <Link
                    to={getCaytegoryLink(
                      `${generalCategory.id}-${subCategory.id}`,
                      subCategory.title
                    )}
                  >
                    {subCategory.title}
                  </Link>
                </Breadcrumb.Item>
                {category !== undefined ? (
                  <Breadcrumb.Item>
                    <Link
                      to={getCaytegoryLink(
                        `${generalCategory.id}-${subCategory.id}-${category.id}`,
                        category.title
                      )}
                    >
                      {category.title}
                    </Link>
                  </Breadcrumb.Item>
                ) : (
                  <Fragment />
                )}
              </Fragment>
            ) : (
              <Fragment />
            )}
          </Fragment>
        ) : (
          <Fragment />
        )}
      </Breadcrumb>
    );
  };

  return (
    <DocumentTitle title={decodeURIComponent(title)}>
      <Layout>
        <MobileView>
          <Drawer
            title={I18n.general.filters}
            visible={areMobileFiltersVisible}
            onClose={closeMobileFilters}
            closable={true}
            placement="bottom"
            height="75%"
          >
            <Filters
              selectedColors={selectedColors}
              selectedSizes={selectedSizes}
            />
          </Drawer>
        </MobileView>
        <BrowserView>
          <Sider
            id="fiters-sider"
            className="sider"
            style={{ right: width > 1366 ? (width - 1366) / 2 : 0, backgroundColor: 'aliceblue' }}
            breakpoint="md"
            width={siderWith}
            collapsedWidth={0}

            onCollapse={(collapsed, type) => {
              if (collapsed) setContentMarginRight(0);
              else setContentMarginRight(siderWith);
            }}
          >
            <Filters
              selectedColors={selectedColors}
              selectedSizes={selectedSizes}
            />
          </Sider>
        </BrowserView>
        <Content style={{ marginRight: contentMarginRight }}>
          <List
            className="products-list"
            grid={{ gutter: 8, xs: 2, sm: 2, md: 3, lg: 4, column: 4 }}
            style={{ paddingTop: 5 }}
            loading={loading === "pending"}
            dataSource={products}
            header={
              <div>
                {renderBreadCrumb()}
                <Divider style={{ margin: 0 }} />
                <Row
                  style={{ paddingTop: 7 }}
                  justify="space-between"
                  align="middle"
                >
                  <div style={{ paddingTop: 0 }}>
                    <Typography.Text>
                      {I18n.productsList.sortBy}:
                    </Typography.Text>
                    <Select
                      size="small"
                      defaultValue={selectedSort}
                      dropdownMatchSelectWidth
                      bordered={false}
                      onSelect={(value, _) => {
                        query.set("sort", value);
                        history.push({
                          pathname: location.pathname,
                          search: query.toString(),
                        });
                      }}
                    >
                      <Select.Option value={Sort.date}>
                        {I18n.productsList.sort.date}
                      </Select.Option>
                      <Select.Option value={Sort.dateAscending}>
                        {I18n.productsList.sort.dateAscending}
                      </Select.Option>
                      <Select.Option value={Sort.priceAscending}>
                        {I18n.productsList.sort.priceAscending}
                      </Select.Option>
                      <Select.Option value={Sort.priceDescending}>
                        {I18n.productsList.sort.priceDescending}
                      </Select.Option>
                    </Select>
                  </div>
                  {isMobile ? (
                    <Button
                      type="ghost"
                      size="middle"
                      onClick={openMobileFilters}
                    >
                      دسته بندی
                    </Button>
                  ) : (
                    <Fragment />
                  )}
                </Row>
              </div>
            }
            renderItem={(item) => (
              <List.Item>
                <ProductCard product={item} />
              </List.Item>
            )}
            pagination={{
              className: "products-list-pagination",
              showSizeChanger: false,
              current: page,
              hideOnSinglePage: true,
              total: productsCount,
              pageSize: pageSize,
              onChange: (page, _) => {
                query.set("page", page.toString());
                history.push({
                  pathname: location.pathname,
                  search: query.toString(),
                });
              },
            }}
          />
        </Content>
      </Layout>
    </DocumentTitle>
  );
}
